import React, { useEffect, useState, useCallback } from 'react';
import api, { fileUrl } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Search, MoreHorizontal, Ban, Clock, RotateCcw, List, Image as ImageIcon, ShieldCheck, ShieldAlert, UserCog, Flag, ChevronLeft, ChevronRight, Pencil, BellRing, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { usernameHasProfanity, isReservedUsername } from '@/lib/profanity';

const STATUS_COLOR = { active: '#7A9A78', suspended: '#D99E4A', banned: '#9A4A4A' };
const ROLE_COLOR = { user: '#8A8F87', moderator: '#7A6FC8', admin: '#2C5545', owner: '#D99E4A' };
const ROLE_LEVELS = { user: 0, moderator: 1, admin: 2, owner: 3 };
const ROLE_OPTIONS = ['user', 'moderator', 'admin', 'owner'];
const GROUPS = [
  { key: 'user', label: 'Users', min: 0 },
  { key: 'moderator', label: 'Moderators', min: 1 },
  { key: 'admin', label: 'Admins', min: 2 },
];
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function UsersPanel() {
  const { user: me } = useAuth();
  const myLevel = ROLE_LEVELS[me?.role] ?? 0;
  const isOwner = me?.role === 'owner';
  const canModerate = myLevel >= 2; // admin/owner can ban/suspend/report

  const visibleGroups = GROUPS.filter(g => myLevel >= g.min);
  // Default sub-tab = the viewer's own role group (owner is grouped under "admin").
  const defaultGroup = me?.role === 'owner' ? 'admin' : (me?.role || 'user');

  const [group, setGroup] = useState(defaultGroup);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 25 });
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);

  const [banTarget, setBanTarget] = useState(null);
  const [banMode, setBanMode] = useState('ban');
  const [reason, setReason] = useState('');
  const [days, setDays] = useState(7);
  const [postsUser, setPostsUser] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [roleTarget, setRoleTarget] = useState(null);
  const [newRole, setNewRole] = useState('user');
  const [reportTarget, setReportTarget] = useState(null);
  const [usernameTarget, setUsernameTarget] = useState(null);
  const [newUsername, setNewUsername] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameReason, setRenameReason] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/users', { params: { group, q: q || undefined, page, limit: 25 } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load users'); }
    finally { setLoading(false); }
  }, [group, q, page]);

  useEffect(() => { load(); }, [group, page]); // eslint-disable-line
  useEffect(() => { api.get('/admin/users/counts').then(r => setCounts(r.data)).catch(() => {}); }, []);
  const refresh = async () => { await load(); api.get('/admin/users/counts').then(r => setCounts(r.data)).catch(() => {}); };

  const changeGroup = (g) => { setGroup(g); setPage(1); };
  const submitSearch = () => { setPage(1); load(); };

  const openBan = (u, mode) => { setBanTarget(u); setBanMode(mode); setReason(''); setDays(7); };
  const submitBan = async () => {
    try {
      if (banMode === 'ban') await api.post(`/admin/users/${banTarget.user_id}/ban`, { reason });
      else await api.post(`/admin/users/${banTarget.user_id}/suspend`, { days: Number(days), reason });
      toast.success(banMode === 'ban' ? 'User banned' : 'User suspended');
      setBanTarget(null); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const unban = async (u) => {
    try { await api.post(`/admin/users/${u.user_id}/unban`); toast.success('User reinstated'); await refresh(); }
    catch { toast.error('Failed'); }
  };
  const viewPosts = async (u) => {
    setPostsUser(u); setUserPosts([]);
    try { const r = await api.get(`/admin/users/${u.user_id}/posts`); setUserPosts(r.data); } catch (e) { console.error('Failed to load user posts:', e); toast.error('Could not load user posts'); }
  };
  const openRole = (u) => { setRoleTarget(u); setNewRole(u.role || 'user'); };
  const submitRole = async () => {
    try {
      await api.patch(`/admin/users/${roleTarget.user_id}/role`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      setRoleTarget(null); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change role'); }
  };
  const submitReport = async () => {
    if (!reason.trim()) { toast.error('Add a reason'); return; }
    try {
      await api.post(`/admin/users/${reportTarget.user_id}/report-to-owner`, { reason });
      toast.success('Reported to owner');
      setReportTarget(null); setReason('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const openUsername = (u) => { setUsernameTarget(u); setNewUsername(u.username || ''); };
  const usernameValid = /^[A-Za-z0-9_-]{6,24}$/.test(newUsername) && !usernameHasProfanity(newUsername) && !isReservedUsername(newUsername);
  const submitUsername = async () => {
    try {
      await api.patch(`/admin/users/${usernameTarget.user_id}/username`, { username: newUsername.trim() });
      toast.success(`Username updated to @${newUsername.trim()}`);
      setUsernameTarget(null); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not change username'); }
  };
  const submitRenameRequest = async () => {
    try {
      await api.post(`/admin/users/${renameTarget.user_id}/request-username-change`, { reason: renameReason });
      toast.success('Rename request sent');
      setRenameTarget(null); setRenameReason('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openDelete = (u) => { setDeleteTarget(u); setDeleteConfirm(''); };
  const submitDelete = async () => {
    if (deleteConfirm !== 'DELETE') return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.user_id}`);
      toast.success(`Deleted @${deleteTarget.username || deleteTarget.name}`);
      setDeleteTarget(null); setDeleteConfirm(''); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not delete account'); }
    finally { setDeleting(false); }
  };

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.limit || 25)));

  return (
    <div data-testid="users-panel">
      <h2 className="font-display font-bold text-2xl text-brand-ink mb-1">User management</h2>
      <p className="text-brand-slate text-sm mb-5">
        {canModerate ? 'Search, review activity, enforce bans' : 'Browse the community'}{isOwner ? ', and manage staff roles' : ''}.
      </p>

      {/* Role sub-tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {visibleGroups.map(g => (
          <button key={g.key} data-testid={`users-tab-${g.key}`} onClick={() => changeGroup(g.key)}
            className={`rounded-full px-4 py-2 text-sm font-medium transition ${group === g.key ? 'bg-brand text-white' : 'glass-panel text-brand-ink hover:bg-white'}`}>
            {g.label}{counts[g.key] != null && <span className="ml-1.5 opacity-70">{counts[g.key]}</span>}
          </button>
        ))}
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-dim" />
        <Input data-testid="users-search" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === 'Enter' && submitSearch()}
          placeholder="Search by email, name, username, or user ID…" className="pl-9 rounded-full bg-white/70" />
      </div>

      <div className="glass-panel rounded-3xl overflow-hidden">
        <div className="hidden md:grid grid-cols-[1fr_1fr_90px_110px_110px_44px] gap-3 px-4 py-3 text-[11px] uppercase tracking-wider text-brand-dim border-b border-black/5">
          <div>User</div><div>Email</div><div>Posts</div><div>Role</div><div>Status</div><div></div>
        </div>
        {loading ? <div className="p-8 text-center text-brand-slate text-sm">Loading…</div>
          : data.items.length === 0 ? <div className="p-8 text-center text-brand-slate text-sm" data-testid="users-empty">No one here yet.</div>
          : data.items.map(u => {
            const targetIsStaff = ROLE_LEVELS[u.role] >= 1;
            const isSelf = u.user_id === me?.user_id;
            const canManageTarget = myLevel >= (ROLE_LEVELS[u.role] ?? 0);
            return (
            <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className="grid grid-cols-[1fr_auto] md:grid-cols-[1fr_1fr_90px_110px_110px_44px] gap-3 px-4 py-3 items-center border-b border-black/5 last:border-0">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded-full bg-brand text-white text-xs flex items-center justify-center shrink-0">{(u.username || u.name || 'U')[0].toUpperCase()}</div>
                <div className="min-w-0">
                  <div className="font-medium text-brand-ink truncate">@{u.username || '—'}</div>
                  <div className="text-xs text-brand-slate truncate">{u.name}</div>
                </div>
              </div>
              <div className="hidden md:block text-xs text-brand-slate truncate">{u.email}</div>
              <div className="hidden md:block text-sm text-brand-ink">{u.post_count}</div>
              <Badge data-testid={`user-role-${u.user_id}`} className="border-0 justify-self-start" style={{ background: (ROLE_COLOR[u.role] || '#888') + '22', color: ROLE_COLOR[u.role] || '#888' }}>{u.role}</Badge>
              <Badge className="border-0 justify-self-start" style={{ background: (STATUS_COLOR[u.status] || '#888') + '22', color: STATUS_COLOR[u.status] || '#888' }}>{u.status}</Badge>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid={`user-actions-${u.user_id}`} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center"><MoreHorizontal className="w-4 h-4" /></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-2xl">
                  <DropdownMenuItem onClick={() => viewPosts(u)}><List className="w-4 h-4 mr-2" /> View posts ({u.post_count})</DropdownMenuItem>

                  {isOwner && !isSelf && (
                    <DropdownMenuItem data-testid={`user-role-change-${u.user_id}`} onClick={() => openRole(u)}><UserCog className="w-4 h-4 mr-2 text-brand" /> Change role</DropdownMenuItem>
                  )}

                  {canModerate && canManageTarget && <>
                    <DropdownMenuItem data-testid={`user-username-${u.user_id}`} onClick={() => openUsername(u)}><Pencil className="w-4 h-4 mr-2 text-brand" /> Change username</DropdownMenuItem>
                    {!isSelf && (
                      <DropdownMenuItem data-testid={`user-request-rename-${u.user_id}`} onClick={() => { setRenameTarget(u); setRenameReason(''); }}><BellRing className="w-4 h-4 mr-2 text-[#D99E4A]" /> Request name change</DropdownMenuItem>
                    )}
                  </>}

                  {canModerate && !targetIsStaff && u.status === 'active' && <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid={`user-suspend-${u.user_id}`} onClick={() => openBan(u, 'suspend')}><Clock className="w-4 h-4 mr-2 text-[#D99E4A]" /> Suspend</DropdownMenuItem>
                    <DropdownMenuItem data-testid={`user-ban-${u.user_id}`} onClick={() => openBan(u, 'ban')} className="text-[#9A4A4A]"><Ban className="w-4 h-4 mr-2" /> Ban</DropdownMenuItem>
                  </>}
                  {canModerate && !targetIsStaff && u.status !== 'active' && <DropdownMenuItem data-testid={`user-unban-${u.user_id}`} onClick={() => unban(u)}><RotateCcw className="w-4 h-4 mr-2 text-[#7A9A78]" /> Reinstate</DropdownMenuItem>}

                  {canModerate && targetIsStaff && !isSelf && !isOwner && (
                    <DropdownMenuItem data-testid={`user-report-${u.user_id}`} onClick={() => { setReportTarget(u); setReason(''); }} className="text-[#C86F53]"><Flag className="w-4 h-4 mr-2" /> Report to owner</DropdownMenuItem>
                  )}

                  {canModerate && !isSelf && (ROLE_LEVELS[u.role] ?? 0) < myLevel && <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid={`user-delete-${u.user_id}`} onClick={() => openDelete(u)} className="text-[#9A4A4A]"><Trash2 className="w-4 h-4 mr-2" /> Delete account</DropdownMenuItem>
                  </>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ); })}

        {/* Pagination */}
        {data.total > data.limit && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-black/5 text-sm text-brand-slate">
            <span>Showing {(page - 1) * data.limit + 1}–{Math.min(page * data.limit, data.total)} of {data.total}</span>
            <div className="flex items-center gap-2">
              <button data-testid="users-prev" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}
                className="w-8 h-8 rounded-full glass-panel flex items-center justify-center disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
              <span>{page} / {totalPages}</span>
              <button data-testid="users-next" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                className="w-8 h-8 rounded-full glass-panel flex items-center justify-center disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Ban / suspend dialog */}
      <Dialog open={!!banTarget} onOpenChange={(o) => !o && setBanTarget(null)}>
        <DialogContent className="rounded-3xl" data-testid="ban-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{banMode === 'ban' ? 'Ban user' : 'Suspend user'}</DialogTitle>
            <DialogDescription>{banMode === 'ban' ? 'Permanently block' : 'Temporarily block'} @{banTarget?.username || banTarget?.name}. Their active posts will be hidden.</DialogDescription>
          </DialogHeader>
          {banMode === 'suspend' && (
            <div>
              <label className="text-xs uppercase tracking-wider text-brand-dim">Suspend for (days)</label>
              <Input data-testid="suspend-days" type="number" min={1} value={days} onChange={e => setDays(e.target.value)} className="mt-1 rounded-xl" />
            </div>
          )}
          <Input data-testid="ban-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (optional)" className="rounded-xl" />
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setBanTarget(null)}>Cancel</Button>
            <Button data-testid="ban-confirm" onClick={submitBan} className="rounded-full text-white" style={{ background: banMode === 'ban' ? '#9A4A4A' : '#D99E4A' }}>
              {banMode === 'ban' ? 'Ban user' : `Suspend ${days}d`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role change confirmation */}
      <Dialog open={!!roleTarget} onOpenChange={(o) => !o && setRoleTarget(null)}>
        <DialogContent className="rounded-3xl" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-brand" /> Change role</DialogTitle>
            <DialogDescription>
              You're about to change <span className="font-semibold">@{roleTarget?.username || roleTarget?.name}</span> ({roleTarget?.email}) from
              <span className="font-semibold"> {roleTarget?.role}</span> to the role below. This grants real permissions — please confirm.
            </DialogDescription>
          </DialogHeader>
          <Select value={newRole} onValueChange={setNewRole}>
            <SelectTrigger className="rounded-xl" data-testid="role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map(r => <SelectItem key={r} value={r} data-testid={`role-opt-${r}`}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setRoleTarget(null)}>Cancel</Button>
            <Button data-testid="role-confirm" onClick={submitRole} disabled={newRole === roleTarget?.role} className="rounded-full bg-brand hover:bg-brand-hover text-white">
              <ShieldCheck className="w-4 h-4 mr-2" /> Confirm change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change username (admin/owner) */}
      <Dialog open={!!usernameTarget} onOpenChange={(o) => !o && setUsernameTarget(null)}>
        <DialogContent className="rounded-3xl" data-testid="username-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Pencil className="w-5 h-5 text-brand" /> Change username</DialogTitle>
            <DialogDescription>
              Set a new public username for <span className="font-semibold">@{usernameTarget?.username || usernameTarget?.name}</span> ({usernameTarget?.email}).
            </DialogDescription>
          </DialogHeader>
          <Input data-testid="username-input" value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder="6–24 chars · letters, numbers, _ or -" className="rounded-xl" />
          {newUsername && !usernameValid && (
            <p className="text-xs text-[#C86F53]">Must be 6–24 characters (letters, numbers, _ or -), no inappropriate or reserved names.</p>
          )}
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setUsernameTarget(null)}>Cancel</Button>
            <Button data-testid="username-confirm" onClick={submitUsername} disabled={!usernameValid || newUsername.trim() === usernameTarget?.username} className="rounded-full bg-brand hover:bg-brand-hover text-white">
              <ShieldCheck className="w-4 h-4 mr-2" /> Save username
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request username change (sends alert + forces rename) */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="rounded-3xl" data-testid="request-rename-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><BellRing className="w-5 h-5 text-[#D99E4A]" /> Request name change</DialogTitle>
            <DialogDescription>
              Notify <span className="font-semibold">@{renameTarget?.username || renameTarget?.name}</span> that they must pick a new username. They'll be prompted to rename next time they open the app.
            </DialogDescription>
          </DialogHeader>
          <Textarea data-testid="request-rename-reason" value={renameReason} onChange={e => setRenameReason(e.target.value)} placeholder="Reason (optional — shown to the user)" className="rounded-xl min-h-[90px]" />
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button data-testid="request-rename-confirm" onClick={submitRenameRequest} className="rounded-full bg-[#D99E4A] hover:bg-[#c98c38] text-white"><BellRing className="w-4 h-4 mr-2" /> Send request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report staff to owner */}
      <Dialog open={!!reportTarget} onOpenChange={(o) => !o && setReportTarget(null)}>
        <DialogContent className="rounded-3xl" data-testid="report-staff-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Report staff to owner</DialogTitle>
            <DialogDescription>Admins can't ban other staff. Flag @{reportTarget?.username || reportTarget?.name} ({reportTarget?.role}) for the owner to review.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="report-staff-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="What's the concern?" className="rounded-xl min-h-[90px]" />
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setReportTarget(null)}>Cancel</Button>
            <Button data-testid="report-staff-confirm" onClick={submitReport} className="rounded-full bg-[#C86F53] hover:bg-[#b35f45] text-white"><Flag className="w-4 h-4 mr-2" /> Send report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete account (typed confirmation) */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="rounded-3xl" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-[#9A4A4A]"><Trash2 className="w-5 h-5" /> Delete account</DialogTitle>
            <DialogDescription>
              Permanently delete <span className="font-semibold">@{deleteTarget?.username || deleteTarget?.name}</span> ({deleteTarget?.email}) and <span className="font-semibold">all their pins, photos, and data</span>. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs uppercase tracking-wider text-brand-dim">Type <span className="font-bold">DELETE</span> to confirm</label>
            <Input data-testid="delete-confirm-input" value={deleteConfirm} onChange={e => setDeleteConfirm(e.target.value)} placeholder="DELETE" className="mt-1 rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button data-testid="delete-confirm-btn" onClick={submitDelete} disabled={deleteConfirm !== 'DELETE' || deleting} className="rounded-full text-white" style={{ background: '#9A4A4A' }}>
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />} Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User posts dialog */}      <Dialog open={!!postsUser} onOpenChange={(o) => !o && setPostsUser(null)}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="user-posts-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Posts by @{postsUser?.username || postsUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {userPosts.length === 0 ? <p className="text-sm text-brand-slate">No posts.</p> : userPosts.map(p => (
              <a key={p.id} href={`/?pin=${p.id}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-xl hover:bg-black/5">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-black/5 flex items-center justify-center shrink-0">
                  {p.photos?.[0] && p.photos[0] !== 'default_yard_sale' ? <img src={fileUrl(p.photos[0])} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-brand-dim" />}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-brand-ink truncate">{p.title}</div>
                  <div className="text-xs text-brand-slate">{p.status} · {p.category === 'free' ? 'Free' : 'Yard Sale'}</div>
                </div>
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
